import polygonClipping           from 'polygon-clipping';
import { circleRingLngLat }      from '@tokamap/core';
import { generateId }            from '@tokamap/core';
import { lngLatToMercator }      from '@tokamap/core';
import { mercatorToLngLat }      from '@tokamap/core';
import { pointAtPosition }       from '@tokamap/core';
import { positionOfPoint }       from '@tokamap/core';
import { ringVertices }          from '@tokamap/core';
import { withoutClosingPoint }   from '@tokamap/core';
import { shapeToLatestRawFeature } from '@tokamap/core';
import type { LatestRawFeature }  from '@tokamap/core';
import type { LngLat }            from '@tokamap/core';
import type { ShapeOpening }      from '@tokamap/core';
import type { Shape }             from '@tokamap/core';
import shapePlaceId               from '@/TokamapEditor/helpers/shapePlaceId';
import asSingleHistoryEntry       from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import { message }                from '@widesk-ui/hooks/useMessage';

export type MergePlan =
	| { ok: true; feature: LatestRawFeature; layer?: number; removeIds: string[]; keeper: Shape; droppedOpenings: number }
	| { ok: false; reason: string };

/** Les formes qui ont une surface : elles se fusionnent et se découpent, un cercle par son contour. */
export const AREA_KINDS: (Shape['shapeKind'])[] = [undefined, 'polygon', 'rectangle', 'circle'];

/**
 * Prépare la fusion de formes en une seule : le contour est leur union, le reste (style, thème,
 * calque, texte) vient de la plus grande — en général le bâtiment principal, et indépendant de
 * l'ordre des clics. Rien n'est appliqué ici : le plan dit quoi supprimer et quoi créer, ou pourquoi
 * c'est refusé.
 */
/** Ce qui interdit la fusion sans calculer la géométrie, `null` si rien ne s'y oppose encore. */
export function mergeBlocker(shapes: Shape[]): string | null {
	if (shapes.length < 2) return 'Sélectionnez au moins deux formes à fusionner.';

	if (shapes.some(shape => !AREA_KINDS.includes(shape.shapeKind))) {
		return 'Seuls les polygones, rectangles et cercles se fusionnent : retirez les lignes et images de la sélection.';
	}

	// Une forme fusionnée ne peut ouvrir qu'un point d'intérêt : en garder un en silence ferait
	// disparaître le lien de l'autre.
	const placeIds = [...new Set(shapes.map(shapePlaceId).filter(Boolean))];
	if (placeIds.length > 1) {
		return `Ces formes sont liées à ${placeIds.length} points d'intérêt différents : déliez-en avant de fusionner.`;
	}

	return null;
}

export function planMerge(shapes: Shape[]): MergePlan {
	const blocker = mergeBlocker(shapes);
	if (blocker) return { ok: false, reason: blocker };

	// En mètres (EPSG:3857) : c'est là que la tolérance de contact a un sens. Le rapprochement referme
	// les interstices, même entre deux formes qu'une troisième relie déjà ; mais il peut défaire un
	// contact existant, d'où l'union brute en repli.
	const mercatorPolygons = shapes.map(shape => [outline(shape), ...(shape.holes ?? [])].map(ring => closedRing(ring).map(point => lngLatToMercator(point))));
	const union = unionAsOnePolygon(snapToNeighbours(mercatorPolygons)) ?? unionAsOnePolygon(mercatorPolygons);

	if (!union) {
		return { ok: false, reason: 'Ces formes ne se touchent pas : le résultat serait en plusieurs morceaux. Sélectionnez des formes mitoyennes.' };
	}

	const keeper = shapes.reduce((largest, shape) => (ringArea(outline(shape)) > ringArea(outline(largest)) ? shape : largest));
	// Le lien vers le point d'intérêt suit, même s'il était porté par une autre forme que la plus grande.
	const linked = shapes.find(shape => shapePlaceId(shape)) ?? keeper;

	const [outerRing, ...holeRings] = union;
	const [outer, ...holes] = [outerRing!, ...holeRings.filter(isRealHole)].map(ring => ring.map(point => mercatorToLngLat(point as [number, number])));
	const { openings, dropped } = carryOpenings(shapes, [outer!, ...holes]);
	const merged: Shape = {
		...keeper,
		id: generateId(),
		shapeKind: 'polygon',
		coordinates: outer!,
		holes: holes.length ? holes : undefined,
		openings: openings.length ? openings : undefined,
		walled: shapes.some(shape => shape.walled) || openings.length > 0 || undefined,
		entity: linked.entity,
		action: linked === keeper ? keeper.action : linked.action,
	};

	return {
		ok: true,
		feature: shapeToLatestRawFeature(merged),
		layer: keeper.layer,
		removeIds: shapes.map(shape => shape.id),
		keeper,
		droppedOpenings: dropped,
	};
}

/**
 * Reporte les ouvertures des formes fusionnées sur les anneaux du résultat. Une ouverture n'est
 * gardée que si ses deux bouts et son milieu tombent encore sur un même mur : celles d'un mur
 * devenu intérieur à la fusion n'ont plus rien à ouvrir.
 */
function carryOpenings(shapes: Shape[], rings: LngLat[][]): { openings: ShapeOpening[]; dropped: number } {
	const ringsVertices = rings.map(ring => withoutClosingPoint(ring));
	const openings: ShapeOpening[] = [];
	let dropped = 0;

	for (const shape of shapes) {
		for (const opening of shape.openings ?? []) {
			const vertices = ringVertices(shape, opening.ring);
			if (vertices.length < 2) continue;

			const span = ((opening.to - opening.from) % vertices.length + vertices.length) % vertices.length;
			const ends = [opening.from, opening.from + span / 2, opening.to].map(position => pointAtPosition(vertices, position));
			const ring = ringsVertices.findIndex(target => ends.every(point => distanceToRingM(target, point) <= CONTACT_TOLERANCE_M));

			if (ring < 0) {
				dropped++;
				continue;
			}

			openings.push({ ring, ...openingSpan(ringsVertices[ring]!, ends) });
		}
	}

	return { openings, dropped };
}

/**
 * Bouts d'une ouverture reportée sur un anneau, dans le sens où elle passe par son milieu : un contour
 * recalculé peut tourner dans le sens inverse de l'original, et l'ouverture ferait alors le tour du mur.
 */
export function openingSpan(vertices: LngLat[], [start, middle, end]: LngLat[]): { from: number; to: number } {
	const [from, through, to] = [start!, middle!, end!].map(point => positionOfPoint(vertices, point));
	const forward = (a: number, b: number) => ((b - a) % vertices.length + vertices.length) % vertices.length;

	return forward(from!, through!) <= forward(from!, to!) ? { from: from!, to: to! } : { from: to!, to: from! };
}

export function distanceToRingM(vertices: LngLat[], point: LngLat): number {
	const [x0, y0] = lngLatToMercator(point);
	const [x1, y1] = lngLatToMercator(pointAtPosition(vertices, positionOfPoint(vertices, point)));

	return Math.hypot(x1 - x0, y1 - y0);
}

/** Contours de l'union des anneaux, ou `undefined` si elle tient en plusieurs morceaux. */
function unionAsOnePolygon(polygons: [number, number][][][]): [number, number][][] | undefined {
	const [first, ...others] = polygons;

	try {
		const union = polygonClipping.union(first!, ...others);

		return union.length === 1 ? union[0] : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Deux formes qui « se touchent » à l'écran laissent souvent un interstice de quelques centimètres
 * (tracé à la main, tuiles différentes) : l'union les verrait séparées. Chaque sommet assez proche
 * du bord d'une autre forme, contour ou cour, y est ramené et ajouté à ce bord : le mur commun est fait des
 * mêmes points des deux côtés. Sans cet ajout, un sommet posé sur un bord voisin en retombe d'un
 * arrondi, et l'union ne voit qu'un contact en un point.
 */
function snapToNeighbours(polygons: [number, number][][][]): [number, number][][][] {
	type Insertion = { t: number; point: [number, number] };
	const insertions = polygons.map(polygon => polygon.map(ring => ring.map(() => [] as Insertion[])));

	const snapped = polygons.map((polygon, index) => polygon.map(ring => ring.map(point => {
		let best: { point: [number, number]; distance: number; target?: [number, number, number, number] } = { point, distance: CONTACT_TOLERANCE_M };

		polygons.forEach((other, otherIndex) => {
			if (otherIndex === index) return;

			other.forEach((otherRing, ringIndex) => {
				for (let i = 0; i + 1 < otherRing.length; i++) {
					const { point: projected, t } = projectOnSegment(point, otherRing[i]!, otherRing[i + 1]!);
					const distance = Math.hypot(projected[0] - point[0], projected[1] - point[1]);

					if (distance < best.distance) best = { point: projected, distance, target: [otherIndex, ringIndex, i, t] };
				}
			});
		});

		if (best.target) {
			const [otherIndex, ringIndex, segment, t] = best.target;
			if (t > 0 && t < 1) insertions[otherIndex]![ringIndex]![segment]!.push({ t, point: best.point });
		}

		return best.point;
	})));

	return snapped.map((polygon, index) => polygon.map((ring, ringIndex) => ring.flatMap((point, i) => [
		point,
		...insertions[index]![ringIndex]![i]!.sort((a, b) => a.t - b.t).map(({ point: inserted }) => inserted),
	])));
}

/**
 * Un interstice entre des formes jointives à l'œil, refermé de tous côtés par l'union, en ressort
 * comme un trou de quelques centimètres de large : affiché, il dessinerait une séparation là où
 * l'utilisateur voit un mur commun.
 */
function isRealHole(ring: [number, number][]): boolean {
	let perimeter = 0;
	for (let i = 0; i + 1 < ring.length; i++) {
		perimeter += Math.hypot(ring[i + 1]![0] - ring[i]![0], ring[i + 1]![1] - ring[i]![1]);
	}

	return perimeter > 0 && (2 * mercatorArea(ring)) / perimeter > MIN_HOLE_WIDTH_M;
}

/** Largeur moyenne (deux fois l'aire sur le périmètre) sous laquelle un trou n'est qu'un interstice. */
const MIN_HOLE_WIDTH_M = 1;

export function mercatorArea(ring: [number, number][]): number {
	let sum = 0;
	for (let i = 0; i + 1 < ring.length; i++) sum += ring[i]![0] * ring[i + 1]![1] - ring[i + 1]![0] * ring[i]![1];

	return Math.abs(sum) / 2;
}

/** Au-delà, deux formes sont vraiment séparées (en mètres de la projection, ~1 m au sol en France). */
const CONTACT_TOLERANCE_M = 1.5;

function projectOnSegment(point: [number, number], from: [number, number], to: [number, number]): { point: [number, number]; t: number } {
	const dx = to[0] - from[0];
	const dy = to[1] - from[1];
	const lengthSquared = dx * dx + dy * dy;
	const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point[0] - from[0]) * dx + (point[1] - from[1]) * dy) / lengthSquared));

	// Les extrémités sont rendues telles quelles : recalculées, elles différeraient d'un arrondi du sommet voisin.
	if (t === 0) return { point: from, t };
	if (t === 1) return { point: to, t };

	return { point: [from[0] + t * dx, from[1] + t * dy], t };
}

export function outline(shape: Shape): LngLat[] {
	return shape.shapeKind === 'circle' ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates as LngLat[];
}

export function closedRing(points: LngLat[]): [number, number][] {
	const [first] = points;
	const last = points[points.length - 1];

	return first && last && (first[0] !== last[0] || first[1] !== last[1]) ? [...points, first] : points;
}

function ringArea(points: LngLat[]): number {
	let sum = 0;
	for (let i = 0; i < points.length; i++) {
		const [x0, y0] = points[i]!;
		const [x1, y1] = points[(i + 1) % points.length]!;
		sum += x0 * y1 - x1 * y0;
	}

	return Math.abs(sum) / 2;
}

/** Fusionne les formes données, en une seule entrée d'historique, et sélectionne le résultat. */
export async function mergeShapes(shapeIds: string[]): Promise<void> {
	const instance = currentEditorStore.get;
	if (!instance) return;

	const shapes = shapeIds
		.map(id => instance.shapeStore.get(id))
		.filter((shape): shape is Shape => !!shape);
	const plan = planMerge(shapes);

	if (!plan.ok) return void message.warning(plan.reason);

	await asSingleHistoryEntry(() => {
		instance.deleteShape(plan.removeIds);
		instance.addShapeFromFeature(plan.feature, { layer: plan.layer });
	});

	currentEditorStore.requestShapeSelection(String(plan.feature.geometry.properties?.id));
	message.success(`${plan.removeIds.length} formes fusionnées${plan.droppedOpenings ? `, ${plan.droppedOpenings} ouverture${plan.droppedOpenings > 1 ? 's' : ''} retirée${plan.droppedOpenings > 1 ? 's' : ''} : leur mur est devenu intérieur` : ''}.`);
}
